/**
 * Shared API error extraction.
 *
 * The backend returns errors in this shape (see ValidationError and the
 * controllers under modules/*):
 *
 *   {
 *     success: false,
 *     error: {
 *       code: 'VALIDATION_ERROR',
 *       message: 'Validation failed',
 *       statusCode: 400,
 *       details: [
 *         { field: 'uoms', message: 'Exactly one UOM must be base.', code: 'custom' }
 *       ]
 *     }
 *   }
 *
 * Prior to this helper, callers only surfaced `error.message` — which meant
 * the field-level `details[]` were silently dropped, so users saw generic
 * "Save failed" toasts and had no way to know what to fix.
 */

export interface ApiFieldError {
    field: string;
    message: string;
    code?: string;
}

export interface ExtractedApiError {
    /** Short toast title, e.g. "Save failed (2 issues)" */
    title: string;
    /** Full message, ready for the toast body. Uses " • " as separator. */
    message: string;
    /** Structured field-level errors, for inline form highlighting (Step 3). */
    fieldErrors: ApiFieldError[];
    code?: string;
    statusCode?: number;
}

export function extractApiError(
    err: unknown,
    fallbackTitle = 'Request failed',
): ExtractedApiError {
    const anyErr = err as any;
    const apiError = anyErr?.response?.data?.error;

    // No structured API error → fall back to the raw message
    if (!apiError) {
        const rawMessage =
            anyErr?.message ||
            (typeof err === 'string' ? err : 'An unexpected error occurred.');
        return {
            title: fallbackTitle,
            message: rawMessage,
            fieldErrors: [],
        };
    }

    const topMessage: string = apiError.message || fallbackTitle;
    const rawDetails = Array.isArray(apiError.details) ? apiError.details : [];

    const fieldErrors: ApiFieldError[] = rawDetails.map((d: any) => ({
        field: String(d?.field ?? ''),
        message: String(d?.message ?? ''),
        code: d?.code,
    }));

    // The Toast component renders `message` inside a single <p> with no
    // whitespace-pre-line, so newlines collapse. Join with " • " instead.
    const parts: string[] = [topMessage];
    for (const fe of fieldErrors) {
        const prefix = fe.field ? `${humanizeFieldName(fe.field)}: ` : '';
        parts.push(`${prefix}${fe.message}`);
    }

    return {
        title:
            fieldErrors.length > 0
                ? `${fallbackTitle} (${fieldErrors.length} issue${fieldErrors.length === 1 ? '' : 's'})`
                : fallbackTitle,
        message: parts.filter(Boolean).join(' • '),
        fieldErrors,
        code: apiError.code,
        statusCode: apiError.statusCode,
    };
}

/** Convenience: only the message string. */
export function extractErrorMessage(err: unknown): string {
    return extractApiError(err).message;
}

/**
 * "uoms.0.conversionRate"  →  "Conversion Rate"
 * "supplierEmail"          →  "Supplier Email"
 * "items[2].quantity"      →  "Quantity"
 */
function humanizeFieldName(path: string): string {
    if (!path) return '';

    const segments = path
        .replace(/\[(\d+)\]/g, '')
        .split('.')
        .filter((seg) => seg && !/^\d+$/.test(seg));

    const last = segments[segments.length - 1] || path;

    return last
        .replace(/_/g, ' ')
        .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
        .replace(/^./, (c) => c.toUpperCase())
        .trim();
}